import { useState } from 'react';
import { useCourses, useStripe, UserProfileDropdown, getLocalizedTitle, getLocalizedDescription, useDocumentDirection, useWebinars } from '@immopersian/common';
import { useNavigate } from 'react-router-dom';
import { Crown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LoadingScreen } from '../components/LoadingScreen';
import { WebinarCountdownBanner } from '../components/WebinarCountdownBanner';
import { WebinarDetailModal } from '../components/WebinarDetailModal';
import type { Webinar } from '@immopersian/common';

export default function CoursesListPage() {
  const { data: courses, isLoading: isLoadingCourses } = useCourses();
  const { data: webinars } = useWebinars();
  const { hasAccessToCourse, isLoadingPurchases } = useStripe();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [selectedWebinar, setSelectedWebinar] = useState<Webinar | null>(null);

  // Ensure document direction is set correctly for RTL languages
  useDocumentDirection();

  // Always navigate to course detail page - it will handle free sessions and purchase flow
  const handleCourseClick = (courseId: string) => {
    navigate(`/course/${courseId}`);
  };

  const handleWebinarClick = (webinar: Webinar) => {
    setSelectedWebinar(webinar);
  };

  const handleCloseModal = () => {
    setSelectedWebinar(null);
  };

  const handleJoinWebinar = (webinarId: string) => {
    setSelectedWebinar(null);
    navigate(`/webinar/${webinarId}`);
  };

  // Filter webinars to only show valid ones
  const displayableWebinars = webinars?.filter((webinar) => {
    const now = new Date();
    const validUntil = webinar.valid_until ? new Date(webinar.valid_until) : null;
    const hasExpired = validUntil ? now > validUntil : false;

    // Don't show expired webinars (past valid_until date)
    if (hasExpired) {
      return false;
    }

    // Show all non-expired webinars (upcoming, live, or replay with/without recording)
    return true;
  }) || [];

  // Show loading while courses or purchases are loading
  const isLoading = isLoadingCourses || isLoadingPurchases;

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-brand-dark shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">{t('app.name')}</h1>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <UserProfileDropdown onNavigateToSettings={() => navigate('/settings')} />
          </div>
        </div>
      </header>

      {/* Webinar countdown banner */}
      {displayableWebinars.length > 0 && (
        <WebinarCountdownBanner
          webinars={displayableWebinars}
          onWebinarClick={handleWebinarClick}
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-3xl font-bold mb-8 text-brand-dark">{t('courses.title')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses?.map((course) => {
            const isFree = !course.price_id || course.price_id === '0';
            const hasAccess = hasAccessToCourse(course.id, course.price_id);
            const localizedTitle = getLocalizedTitle(course, i18n.language);
            const localizedDescription = getLocalizedDescription(course, i18n.language);
            
            return (
              <div
                key={course.id}
                onClick={() => handleCourseClick(course.id)}
                className="bg-brand-beige rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden relative border border-brand-beige hover:border-brand-terracotta cursor-pointer"
              >
                {!isFree && (
                  <div className="absolute top-3 right-3 bg-brand-terracotta text-white rounded-full p-2 z-10 shadow-lg">
                    <Crown className="w-5 h-5" />
                  </div>
                )}
                {course.thumbnail_url && (
                  <img
                    src={course.thumbnail_url}
                    alt={localizedTitle}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-brand-dark">{localizedTitle}</h3>
                  <div className="prose prose-sm prose-gray max-w-none line-clamp-3 text-brand-dark">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {localizedDescription}
                    </ReactMarkdown>
                  </div>
                  {!isFree && !hasAccess && (
                    <div className="mt-4 text-sm text-brand-terracotta font-medium">
                      {t('courses.premiumCourse')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {(!courses || courses.length === 0) && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('courses.noCourses')}</p>
          </div>
        )}
      </main>

      {/* Webinar detail modal */}
      {selectedWebinar && (
        <WebinarDetailModal
          webinar={selectedWebinar}
          onClose={handleCloseModal}
          onJoin={handleJoinWebinar}
        />
      )}
    </div>
  );
}
